/**
 * P3 릴레이 진행기 — 순수 상태 기계(SR-MODE-07 · ARC 부록 B · FIELD-DEC-003 §4). 커널 의존 없음.
 *
 * 입력: 틱 경계마다 step(tick, 관측) — 관측은 **지금 구간 하나**의 판정(룸 서버 RelayCtl 이 커널 상태에서 만든다:
 *   나감 상자 도달 · 물체 추락 · 학생 접속). 명령(시작 · 일시정지 · 재시도 · 건너뛰기)은 큐에 넣고 다음 step 머리에서
 *   넣은 순서대로 적용한다. 출력: 이벤트(틱 · 종류 · 구간) — 같은 (틱, 명령, 관측) 열이면 같은 이벤트 열이다(결정론).
 *
 *   Setup ─start→ Armed(0) ─armTicks→ Running(0) ─나감→ Done(0) · Armed(1) … Done(n−1) → AllDone
 *                                         └─timeout·lost→ Failed(i) ─retry(학생·교사·자동)→ Armed(i)
 *   건너뛰기(교사 · 학생 부재) — Armed·Running·Failed(i) → Skipped(i) · Armed(i+1)
 *   교사가 그 구간 학생을 뺐다(분리 · 관전 — 관측 removed) → 기다리지 않고 Skipped(i · by teacher)(2026-09-30)
 *
 * 시간은 틱 차이가 아니라 **이 기계가 센 step 수**로 잰다 — 일시정지(룸 pause 로 step 을 안 부르거나 pause 명령)
 * 동안은 타임아웃·Armed 창·자동 재시도가 멈춘다. 구간 번호는 0 부터(화면은 +1).
 */
import type { RelayDef, RelayTrigger } from "./types";

export const RELAY_TRIGGERS: readonly RelayTrigger[] = ["ball", "lever", "zone"];
/** SR-MODE-07 30단 */
export const RELAY_MAX_STAGES = 30;

/** 교사 배치 한 칸(FS-08-01) — 기계 번호 · 들어오는 것 · 내보내는 것 · 타임아웃 */
export interface RelayStagePlan {
  readonly machine: number;
  readonly in: RelayTrigger;
  readonly out: RelayTrigger;
  /** 없으면 기본(stageTimeoutS) */
  readonly timeoutS?: number;
}

export type RelayIssueCode = "count" | "mismatch" | "duplicate" | "trigger" | "timeout";

export interface RelayIssue {
  /** 문제 구간(0 부터) · 전체 문제면 null */
  readonly stage: number | null;
  readonly code: RelayIssueCode;
  readonly message: string;
}

export interface RelayRules {
  readonly minStages?: number;
  readonly maxStages?: number;
  readonly triggers?: readonly RelayTrigger[];
  readonly timeoutChoicesS?: readonly number[];
}

/**
 * 배치 정합 검사(FS-08-01 불일치 배너) — 구간 i 가 내보내는 것 = 구간 i+1 이 들어오는 것.
 * 첫 구간의 들어오는 것은 출발 장치가, 마지막 구간의 내보내는 것은 결승 장치가 받는다(아무 종류나).
 */
export function checkRelayChain(
  stages: readonly RelayStagePlan[],
  rules: RelayRules = {},
): RelayIssue[] {
  const out: RelayIssue[] = [];
  const min = rules.minStages ?? 2;
  const max = Math.min(rules.maxStages ?? RELAY_MAX_STAGES, RELAY_MAX_STAGES);
  if (stages.length < min || stages.length > max)
    out.push({
      stage: null,
      code: "count",
      message: `구간 수 ${stages.length} — ${min}~${max} 여야 한다`,
    });
  const allowed = rules.triggers ?? RELAY_TRIGGERS;
  const seen = new Map<number, number>();
  stages.forEach((s, i) => {
    for (const t of [s.in, s.out])
      if (!allowed.includes(t))
        out.push({ stage: i, code: "trigger", message: `구간 ${i + 1}: 쓸 수 없는 종류 ${t}` });
    const prev = seen.get(s.machine);
    if (prev !== undefined)
      out.push({
        stage: i,
        code: "duplicate",
        message: `구간 ${i + 1}: 기계 ${s.machine} 이 구간 ${prev + 1} 에도 있다`,
      });
    else seen.set(s.machine, i);
    if (
      s.timeoutS !== undefined &&
      (!(s.timeoutS > 0) || (rules.timeoutChoicesS && !rules.timeoutChoicesS.includes(s.timeoutS)))
    )
      out.push({ stage: i, code: "timeout", message: `구간 ${i + 1}: 타임아웃 ${s.timeoutS}` });
    const next = stages[i + 1];
    if (next && s.out !== next.in)
      out.push({
        stage: i + 1,
        code: "mismatch",
        message: `구간 ${i + 1} 이 내보내는 것(${s.out}) ≠ 구간 ${i + 2} 이 들어오는 것(${next.in})`,
      });
  });
  return out;
}

/** 시간 규칙(틱) — 모드 정의 relay 블록에서 */
export interface RelayTiming {
  readonly tickRate: number;
  readonly stageTimeoutS: number;
  readonly armS: number;
  /** 0 = 끔 */
  readonly autoRetryS: number;
  readonly absentSkipS: number;
}

export const RELAY_DEFAULTS: RelayTiming = {
  tickRate: 60,
  stageTimeoutS: 30,
  armS: 0.5,
  autoRetryS: 15,
  absentSkipS: 20,
};

export function relayTiming(def?: RelayDef, tickRate = 60): RelayTiming {
  return {
    tickRate,
    stageTimeoutS: def?.stageTimeoutS ?? RELAY_DEFAULTS.stageTimeoutS,
    armS: def?.armS ?? RELAY_DEFAULTS.armS,
    autoRetryS: def?.autoRetryS ?? RELAY_DEFAULTS.autoRetryS,
    absentSkipS: def?.absentSkipS ?? RELAY_DEFAULTS.absentSkipS,
  };
}

export type RelayStageStatus = "waiting" | "armed" | "running" | "done" | "failed" | "skipped";
export type RelayPhase = "setup" | "running" | "allDone";
export type RelayFailReason = "timeout" | "lost";
export type RelayActor = "teacher" | "student" | "auto";

/** 지금 구간 관측(룸 서버 판정 — 커널 상태 · 틱 경계) */
export interface RelayObservation {
  /** 내보내는 것이 나감 상자에 닿았다(공 도달 · 레버 눌림 · 블록 존 진입) */
  readonly exit?: boolean;
  /** 넘기는 물체가 떨어졌다(lostBelow 아래) */
  readonly lost?: boolean;
  /** 지금 구간 학생이 접속해 있다(기본 true) */
  readonly present?: boolean;
  /**
   * 교사가 지금 구간 학생을 뺐다(분리 · 관전 — 2026-09-30). 부재 20초를 기다리지 않고 바로 건너뛴다(by teacher).
   * 네트워크 끊김은 present false 만 — 20초 규칙 그대로.
   */
  readonly removed?: boolean;
}

export type RelayCommand =
  | { readonly kind: "start" }
  | { readonly kind: "pause" }
  | { readonly kind: "resume" }
  | { readonly kind: "retry"; readonly stage: number; readonly by: "teacher" | "student" }
  | { readonly kind: "skip"; readonly stage: number };

export type RelayReject =
  "locked" | "invalid-plan" | "not-running" | "not-current" | "state" | "paused";

export type RelayCmdResult =
  { readonly ok: true } | { readonly ok: false; readonly reason: RelayReject };

export type RelayEvent =
  | { readonly tick: number; readonly kind: "started"; readonly stages: number }
  | {
      readonly tick: number;
      readonly kind: "armed" | "running";
      readonly stage: number;
      readonly attempt: number;
    }
  | {
      readonly tick: number;
      readonly kind: "done";
      readonly stage: number;
      readonly attempt: number;
      /** Running 틱 수 */
      readonly ticks: number;
    }
  | {
      readonly tick: number;
      readonly kind: "failed";
      readonly stage: number;
      readonly attempt: number;
      readonly reason: RelayFailReason;
    }
  | {
      readonly tick: number;
      readonly kind: "retry";
      readonly stage: number;
      /** 새 시도 번호 */
      readonly attempt: number;
      readonly by: RelayActor;
    }
  | {
      readonly tick: number;
      readonly kind: "skipped";
      readonly stage: number;
      readonly by: "teacher" | "absent";
    }
  | { readonly tick: number; readonly kind: "paused" | "resumed" }
  | {
      readonly tick: number;
      readonly kind: "allDone";
      readonly done: number;
      readonly skipped: number;
      /** 모든 구간 시도 합 */
      readonly attempts: number;
      /** 시작부터 센 step 수(일시정지 뺌) */
      readonly ticks: number;
    };

export interface RelayStageView {
  readonly status: RelayStageStatus;
  readonly attempts: number;
  readonly doneTick: number | null;
  readonly lastFail: RelayFailReason | null;
}

export interface RelayState {
  readonly phase: RelayPhase;
  /** 지금 구간(0 부터) · setup · allDone 이면 null */
  readonly current: number | null;
  readonly paused: boolean;
  readonly stages: readonly RelayStageView[];
}

/** 지금 구간 카운트다운(초) — RelayMachine.timers() */
export interface RelayTimers {
  readonly armLeftS?: number;
  readonly runLeftS?: number;
  readonly retryInS?: number;
  readonly absentLeftS?: number;
}

/** 핫스왑(SR-RUN-05) 허용 — now 바로 · defer 다음 Armed 에 · locked 거절 */
export type RelaySwap = "now" | "defer" | "locked";

interface StageState {
  status: RelayStageStatus;
  attempts: number;
  doneTick: number | null;
  lastFail: RelayFailReason | null;
}

const ticksOf = (s: number, rate: number) => Math.max(0, Math.round(s * rate));

export class RelayMachine {
  private plan: readonly RelayStagePlan[];
  private stages: StageState[];
  private phase: RelayPhase = "setup";
  private current: number | null = null;
  private paused = false;
  private readonly queue: RelayCommand[] = [];
  /** 지금 상태로 센 step 수(Armed 창 · Running · Failed) */
  private phaseSteps = 0;
  private absentSteps = 0;
  private totalSteps = 0;
  /** 지금 상태에 들어간 틱 */
  private enteredTick = -1;
  private readonly armTicks: number;
  private readonly autoRetryTicks: number;
  private readonly absentTicks: number;

  constructor(
    plan: readonly RelayStagePlan[],
    private readonly timing: RelayTiming = RELAY_DEFAULTS,
    private readonly rules: RelayRules = {},
  ) {
    this.plan = [...plan];
    this.stages = this.fresh();
    this.armTicks = ticksOf(timing.armS, timing.tickRate);
    this.autoRetryTicks = ticksOf(timing.autoRetryS, timing.tickRate);
    this.absentTicks = Math.max(1, ticksOf(timing.absentSkipS, timing.tickRate));
  }

  /** Setup 에서만 배치를 바꾼다(실행 뒤 잠금) — 정합 문제는 돌려주고 초안으로 둔다(시작만 막힌다) */
  setPlan(plan: readonly RelayStagePlan[]): RelayCmdResult & { readonly issues: RelayIssue[] } {
    if (this.phase !== "setup") return { ok: false, reason: "locked", issues: [] };
    this.plan = [...plan];
    this.stages = this.fresh();
    return { ok: true, issues: checkRelayChain(this.plan, this.rules) };
  }

  get stagePlan(): readonly RelayStagePlan[] {
    return this.plan;
  }

  /** 명령 받기 — 지금 상태로 검사해 받으면 큐에(다음 step 머리에서 적용 · 그때 다시 검사) */
  command(cmd: RelayCommand): RelayCmdResult {
    const r = this.check(cmd);
    if (r.ok) this.queue.push(cmd);
    return r;
  }

  /** 틱 경계 한 번(물리 스텝 전 · 지난 스텝 뒤 상태 관측) */
  step(tick: number, obs: RelayObservation = {}): RelayEvent[] {
    const ev: RelayEvent[] = [];
    // 시작부터 센 step(일시정지 뺌) — 명령 적용 전에 센다(시작 step 은 0)
    if (this.phase === "running" && !this.paused) this.totalSteps++;
    for (const cmd of this.queue.splice(0)) if (this.check(cmd).ok) this.apply(tick, cmd, ev);
    if (this.phase !== "running" || this.paused || this.current === null) return ev;
    // 이 틱에 명령으로 들어간 상태(시작 · 재시도 · 건너뛰기)는 다음 틱부터 센다
    if (this.enteredTick === tick) return ev;
    const i = this.current;
    const st = this.stages[i]!;
    if (obs.removed) {
      this.skip(tick, i, "teacher", ev);
      return ev;
    }
    this.absentSteps = obs.present === false ? this.absentSteps + 1 : 0;
    if (this.absentSteps >= this.absentTicks) {
      this.skip(tick, i, "absent", ev);
      return ev;
    }
    switch (st.status) {
      case "armed":
        // Armed 창 — 스냅한 물체가 가라앉는 동안 관측을 버린다. 들어간 step 은 세지 않는다(최소 1틱)
        if (++this.phaseSteps >= Math.max(1, this.armTicks)) {
          st.status = "running";
          this.phaseSteps = 0;
          ev.push({ tick, kind: "running", stage: i, attempt: st.attempts });
        }
        break;
      case "running":
        // 같은 틱에 나감과 타임아웃이 겹치면 나감이 이긴다
        this.phaseSteps++;
        if (obs.exit) {
          st.status = "done";
          st.doneTick = tick;
          ev.push({ tick, kind: "done", stage: i, attempt: st.attempts, ticks: this.phaseSteps });
          this.advance(tick, i, ev);
        } else if (obs.lost) this.fail(tick, i, "lost", ev);
        else if (this.phaseSteps >= this.timeoutTicks(i)) this.fail(tick, i, "timeout", ev);
        break;
      case "failed":
        if (this.autoRetryTicks > 0 && ++this.phaseSteps >= this.autoRetryTicks)
          this.retry(tick, i, "auto", ev);
        break;
      default:
        break;
    }
    return ev;
  }

  get state(): RelayState {
    return {
      phase: this.phase,
      current: this.current,
      paused: this.paused,
      stages: this.stages.map((s) => ({ ...s })),
    };
  }

  /** 핫스왑 허용(FIELD-DEC-003 §8) — 지금 구간 Running 이면 다음 Armed 로 미룬다 · 끝난 구간은 잠금 */
  canSwap(stage: number): RelaySwap {
    const s = this.stages[stage];
    if (!s) return "locked";
    if (this.phase === "setup") return "now";
    if (this.phase === "allDone") return "locked";
    switch (s.status) {
      case "waiting":
      case "armed":
      case "failed":
        return "now";
      case "running":
        return "defer";
      default:
        return "locked";
    }
  }

  /**
   * 지금 구간의 남은 시간(초 · 센 step 기준 — 일시정지 동안 멈춘다). 화면 카운트다운용 · 판정에는 쓰지 않는다.
   *  armLeftS = Armed 창 · runLeftS = 타임아웃까지 · retryInS = 자동 재시도까지 · absentLeftS = 부재 건너뜀까지(없을 때만)
   */
  timers(): RelayTimers {
    const i = this.current;
    if (this.phase !== "running" || i === null) return {};
    const rate = this.timing.tickRate;
    const left = (n: number) => Math.max(0, Math.round((n / rate) * 10) / 10);
    const out: { -readonly [K in keyof RelayTimers]: RelayTimers[K] } = {};
    const st = this.stages[i]!;
    if (st.status === "armed") out.armLeftS = left(Math.max(1, this.armTicks) - this.phaseSteps);
    if (st.status === "running") out.runLeftS = left(this.timeoutTicks(i) - this.phaseSteps);
    if (st.status === "failed" && this.autoRetryTicks > 0)
      out.retryInS = left(this.autoRetryTicks - this.phaseSteps);
    if (this.absentSteps > 0) out.absentLeftS = left(this.absentTicks - this.absentSteps);
    return out;
  }

  // ── 안쪽 ───────────────────────────────────────────────────────────

  private fresh(): StageState[] {
    return this.plan.map(() => ({
      status: "waiting",
      attempts: 0,
      doneTick: null,
      lastFail: null,
    }));
  }

  private timeoutTicks(i: number): number {
    const s = this.plan[i]!.timeoutS ?? this.timing.stageTimeoutS;
    return Math.max(1, ticksOf(s, this.timing.tickRate));
  }

  private check(cmd: RelayCommand): RelayCmdResult {
    const no = (reason: RelayReject): RelayCmdResult => ({ ok: false, reason });
    if (cmd.kind === "start") {
      if (this.phase !== "setup") return no("locked");
      return checkRelayChain(this.plan, this.rules).length ? no("invalid-plan") : { ok: true };
    }
    if (this.phase !== "running") return no("not-running");
    if (cmd.kind === "pause") return this.paused ? no("state") : { ok: true };
    if (cmd.kind === "resume") return this.paused ? { ok: true } : no("state");
    if (cmd.stage !== this.current) return no("not-current");
    const status = this.stages[cmd.stage]!.status;
    if (cmd.kind === "retry") {
      // 학생은 실패한 자기 구간만('✓ 다 됐어') · 교사는 실행 중에도 다시 시작할 수 있다
      if (cmd.by === "student") return status === "failed" ? { ok: true } : no("state");
      return status === "failed" || status === "running" ? { ok: true } : no("state");
    }
    return status === "armed" || status === "running" || status === "failed"
      ? { ok: true }
      : no("state");
  }

  private apply(tick: number, cmd: RelayCommand, ev: RelayEvent[]): void {
    switch (cmd.kind) {
      case "start":
        this.phase = "running";
        ev.push({ tick, kind: "started", stages: this.plan.length });
        this.arm(tick, 0, ev);
        return;
      case "pause":
        this.paused = true;
        ev.push({ tick, kind: "paused" });
        return;
      case "resume":
        this.paused = false;
        ev.push({ tick, kind: "resumed" });
        return;
      case "retry":
        this.retry(tick, cmd.stage, cmd.by, ev);
        return;
      case "skip":
        this.skip(tick, cmd.stage, "teacher", ev);
        return;
    }
  }

  private arm(tick: number, i: number, ev: RelayEvent[]): void {
    const st = this.stages[i]!;
    st.status = "armed";
    st.attempts++;
    this.current = i;
    this.phaseSteps = 0;
    this.enteredTick = tick;
    ev.push({ tick, kind: "armed", stage: i, attempt: st.attempts });
  }

  private fail(tick: number, i: number, reason: RelayFailReason, ev: RelayEvent[]): void {
    const st = this.stages[i]!;
    st.status = "failed";
    st.lastFail = reason;
    this.phaseSteps = 0;
    this.enteredTick = tick;
    ev.push({ tick, kind: "failed", stage: i, attempt: st.attempts, reason });
  }

  /** 그 구간만 다시 — 앞 구간(Done · Skipped)은 건드리지 않는다 */
  private retry(tick: number, i: number, by: RelayActor, ev: RelayEvent[]): void {
    ev.push({ tick, kind: "retry", stage: i, attempt: this.stages[i]!.attempts + 1, by });
    this.arm(tick, i, ev);
  }

  private skip(tick: number, i: number, by: "teacher" | "absent", ev: RelayEvent[]): void {
    this.stages[i]!.status = "skipped";
    ev.push({ tick, kind: "skipped", stage: i, by });
    this.advance(tick, i, ev);
  }

  private advance(tick: number, i: number, ev: RelayEvent[]): void {
    this.absentSteps = 0;
    if (i + 1 < this.plan.length) {
      this.arm(tick, i + 1, ev);
      return;
    }
    this.phase = "allDone";
    this.current = null;
    ev.push({
      tick,
      kind: "allDone",
      done: this.stages.filter((s) => s.status === "done").length,
      skipped: this.stages.filter((s) => s.status === "skipped").length,
      attempts: this.stages.reduce((a, s) => a + s.attempts, 0),
      ticks: this.totalSteps,
    });
  }
}
